import { useState, useEffect } from "react";
import axios from "axios";
import {
  Sliders, Users, Building2, Trophy, PartyPopper, Eye, EyeOff, Loader2, CreditCard, Lock,
  LayoutDashboard, Sparkles, FileCheck, FileText, Mail, FolderOpen, Clock, Briefcase,
  BarChart3, User, ShieldCheck, Settings, Cpu, Zap, Rocket,
} from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Capability flags — locked == flag is OFF.
const FLAG_DEFS = [
  { key: "squads_enabled", label: "Squads (peer teams)", icon: Users,
    help: "Let users create & join friend squads with leaderboards." },
  { key: "agencies_enabled", label: "Agencies", icon: Building2,
    help: "Let organizations register agencies and manage candidates." },
  { key: "pricing_enabled", label: "Pricing & Upgrades", icon: CreditCard,
    help: "Lock to hide plan purchases. Locked → customers see 'Temporarily unavailable, please try after some time'. Existing paid users keep their plan." },
  { key: "public_profiles_enabled", label: "Public profiles", icon: Eye,
    help: "Allow viewing another user's profile by username." },
  { key: "global_leaderboard_enabled", label: "Global leaderboard", icon: Trophy,
    help: "Platform-wide public leaderboard. Keep OFF until numbers look strong.", gated: true },
  { key: "job_offer_board_enabled", label: "Job offer celebration board", icon: PartyPopper,
    help: "Public board celebrating users who landed offers. Keep OFF until numbers look strong.", gated: true },
];

// Per-tab locks — ON == LOCKED. Default OFF == available. Toggling ON shows the
// "Temporarily unavailable" popup to non-admin users and blocks the route.
const TAB_LOCK_DEFS = [
  { key: "dashboard_locked", label: "Dashboard", icon: LayoutDashboard },
  { key: "optimize_locked", label: "Optimize", icon: Sparkles },
  { key: "match_score_locked", label: "Match Score", icon: FileCheck },
  { key: "cover_letter_locked", label: "Cover Letter", icon: FileText },
  { key: "application_email_locked", label: "Application Email", icon: Mail },
  { key: "resumes_locked", label: "Resumes", icon: FolderOpen },
  { key: "history_locked", label: "History", icon: Clock },
  { key: "job_tracker_locked", label: "Job Tracker", icon: Briefcase },
  { key: "squads_locked", label: "Squads", icon: Users },
  { key: "agencies_locked", label: "Agencies", icon: Building2 },
  { key: "leaderboard_locked", label: "Leaderboard", icon: Trophy },
  { key: "pricing_locked", label: "Pricing", icon: CreditCard },
  { key: "analytics_locked", label: "Analytics", icon: BarChart3 },
  { key: "profile_locked", label: "Profile", icon: User },
  { key: "privacy_locked", label: "Privacy", icon: ShieldCheck },
  { key: "settings_locked", label: "Settings", icon: Settings },
];

// AI engine locks — ON == LOCKED. When locked, users see "The model is currently locked.
// Please use other model." Admin screens keep REAL technical labels; the customer-facing UI
// brands GLM-5.2 as "NeoCore Lite" and GPT-5.1 as "NeoCore Air" (see frontend/src/lib/models.js).
const MODEL_LOCK_DEFS = [
  { key: "model_glm_locked", label: "GLM-5.2 · NeoCore GLM", icon: Sparkles },
  { key: "model_deepseek_locked", label: "DeepSeek V4 Pro · NeoCore Pro", icon: Rocket },
  { key: "model_gpt_locked", label: "GPT-5.1 (OpenAI) · NeoCore Air", icon: Zap },
];

export default function AdminFeatures() {
  const [flags, setFlags] = useState(null);
  const [loading, setLoading] = useState(true);
  const [savingKey, setSavingKey] = useState(null);

  useEffect(() => {
    axios.get(`${API}/admin/feature-flags`)
      .then((res) => setFlags(res.data))
      .catch(() => toast.error("Failed to load feature flags"))
      .finally(() => setLoading(false));
  }, []);

  const toggle = async (key, label, lock = false, hide = false) => {
    const next = !flags[key];
    setSavingKey(key);
    setFlags((f) => ({ ...f, [key]: next }));
    try {
      const res = await axios.put(`${API}/admin/feature-flags`, { [key]: next });
      setFlags(res.data);
      const word = hide ? (next ? "hidden" : "shown") : lock ? (next ? "locked" : "unlocked") : (next ? "enabled" : "disabled");
      toast.success(`${label} ${word}`);
    } catch {
      toast.error("Failed to update");
      setFlags((f) => ({ ...f, [key]: !next }));
    } finally { setSavingKey(null); }
  };

  const Switch = ({ on, onClick, color, testid }) => (
    <button onClick={onClick} data-testid={testid}
      style={{ width: 46, height: 25, borderRadius: 999, border: "none", cursor: "pointer", position: "relative", flexShrink: 0, background: on ? color : "var(--border-main)", transition: "background 0.2s" }}>
      <span style={{ position: "absolute", top: 3, left: on ? 24 : 3, width: 19, height: 19, borderRadius: "50%", background: "#fff", transition: "left 0.2s" }} />
    </button>
  );

  // Tab row with TWO independent switches: Hide (remove entirely) + Lock (visible but blocked).
  const renderTabRow = ({ key, label, icon: Icon }) => {
    const hideKey = key.replace("_locked", "_hidden");
    const canHide = key !== "dashboard_locked";
    const locked = !!flags[key];
    const hidden = canHide && !!flags[hideKey];
    const status = hidden ? { t: "HIDDEN", bg: "rgba(107,114,128,0.18)", c: "#6b7280", icon: EyeOff }
      : locked ? { t: "LOCKED", bg: "rgba(239,68,68,0.15)", c: "#ef4444", icon: Lock }
      : { t: "AVAILABLE", bg: "rgba(16,185,129,0.15)", c: "#10b981", icon: null };
    const SIcon = status.icon;
    return (
      <div key={key} data-testid={`flag-row-${key}`}
        style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "1rem", padding: "0.85rem 0", borderBottom: "1px solid var(--border-main)" }}>
        <div style={{ display: "flex", gap: "0.8rem", alignItems: "center" }}>
          <div style={{ color: "#6366f1" }}><Icon size={20} /></div>
          <div style={{ fontWeight: 600, display: "flex", alignItems: "center", gap: 8 }}>
            {label}
            <span data-testid={`flag-status-${key}`} style={{ fontSize: "0.66rem", padding: "1px 7px", borderRadius: 999, background: status.bg, color: status.c, display: "inline-flex", alignItems: "center", gap: 4 }}>
              {SIcon && <SIcon size={10} />}{status.t}
            </span>
          </div>
        </div>
        <div style={{ display: "flex", gap: "1.7rem", alignItems: "center" }}>
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 4 }}>
            <span style={{ fontSize: "0.72rem", color: "var(--text-secondary)", textTransform: "uppercase", letterSpacing: 0.5 }}>Hide</span>
            {canHide
              ? <Switch on={hidden} color="#6b7280" testid={`flag-toggle-${hideKey}`} onClick={() => toggle(hideKey, label, false, true)} />
              : <span style={{ fontSize: "0.62rem", color: "var(--text-muted)" }}>—</span>}
          </div>
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 4 }}>
            <span style={{ fontSize: "0.72rem", color: "var(--text-secondary)", textTransform: "uppercase", letterSpacing: 0.5 }}>Lock</span>
            <Switch on={locked} color="#ef4444" testid={`flag-toggle-${key}`} onClick={() => toggle(key, label, true, false)} />
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return <div className="analytics-loading" data-testid="admin-features-loading"><div className="processing-spinner" /></div>;
  }

  // on == the toggle's "active" state. For lock rows, active(on) means LOCKED.
  const renderRow = ({ key, label, icon: Icon, help, gated, lock }) => {
    const on = !!flags[key];
    const activeColor = lock ? "#ef4444" : "#10b981";
    return (
      <div key={key} data-testid={`flag-row-${key}`}
        style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "1rem", padding: "1rem 0", borderBottom: "1px solid var(--border-main)" }}>
        <div style={{ display: "flex", gap: "0.8rem", alignItems: "flex-start" }}>
          <div style={{ color: gated ? "#f59e0b" : "#6366f1", marginTop: 2 }}><Icon size={20} /></div>
          <div>
            <div style={{ fontWeight: 600, display: "flex", alignItems: "center", gap: 8 }}>
              {label}
              {gated && <span style={{ fontSize: "0.66rem", padding: "1px 7px", borderRadius: 999, background: "rgba(245,158,11,0.15)", color: "#f59e0b" }}>ADMIN-GATED</span>}
              {lock && (
                <span data-testid={`flag-status-${key}`}
                  style={{ fontSize: "0.66rem", padding: "1px 7px", borderRadius: 999,
                    background: on ? "rgba(239,68,68,0.15)" : "rgba(16,185,129,0.15)",
                    color: on ? "#ef4444" : "#10b981", display: "inline-flex", alignItems: "center", gap: 4 }}>
                  {on ? <><Lock size={10} /> LOCKED</> : "AVAILABLE"}
                </span>
              )}
            </div>
            {help && <div style={{ fontSize: "0.82rem", color: "var(--text-secondary)", maxWidth: 520 }}>{help}</div>}
          </div>
        </div>
        <button onClick={() => toggle(key, label, lock)} data-testid={`flag-toggle-${key}`} disabled={savingKey === key}
          title={lock ? (on ? "Unlock this tab" : "Lock this tab") : undefined}
          style={{
            width: 48, height: 26, borderRadius: 999, border: "none", cursor: "pointer", position: "relative", flexShrink: 0,
            background: on ? activeColor : "var(--border-main)", transition: "background 0.2s",
          }}>
          {savingKey === key
            ? <Loader2 size={14} className="spin" style={{ color: "#fff", position: "absolute", top: 6, left: 17 }} />
            : <span style={{ position: "absolute", top: 3, left: on ? 25 : 3, width: 20, height: 20, borderRadius: "50%", background: "#fff", transition: "left 0.2s" }} />}
        </button>
      </div>
    );
  };

  return (
    <div className="page-container fade-in" data-testid="admin-features-page">
      <div className="page-header">
        <div>
          <h1 style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}><Sliders size={22} /> Platform Features</h1>
          <p style={{ color: "var(--text-secondary)" }}>Master switches for the social layer. Individual account stats always exist — these control what's <em>visible</em>.</p>
        </div>
      </div>

      <div className="card" style={{ padding: "0.5rem 1.2rem" }}>
        {FLAG_DEFS.map((d) => renderRow(d))}
      </div>

      <div className="page-header" style={{ marginTop: "1.6rem" }}>
        <div>
          <h2 style={{ display: "flex", alignItems: "center", gap: "0.5rem", fontSize: "1.05rem" }}><Sliders size={18} /> Hide or lock individual tabs</h2>
          <p style={{ color: "var(--text-secondary)", fontSize: "0.86rem" }}>
            Two independent switches per tab. <strong>Hide</strong> removes the tab from the sidebar entirely and blocks its URL —
            and hiding a whole section hides everything inside it (e.g. hiding <em>Agencies</em> also hides agency privacy, teams &amp; settings;
            hiding <em>Squads</em> hides squad privacy too). <strong>Lock</strong> keeps the tab visible but shows
            <em> "Temporarily unavailable"</em>. Admins always see everything; the Dashboard can't be hidden.
          </p>
        </div>
      </div>

      <div className="card" data-testid="tab-locks-card" style={{ padding: "0.5rem 1.2rem" }}>
        {TAB_LOCK_DEFS.map((d) => renderTabRow(d))}
      </div>

      <div className="page-header" style={{ marginTop: "1.6rem" }}>
        <div>
          <h2 style={{ display: "flex", alignItems: "center", gap: "0.5rem", fontSize: "1.05rem" }}><Cpu size={18} /> Lock AI models</h2>
          <p style={{ color: "var(--text-secondary)", fontSize: "0.86rem" }}>
            Default is OFF (available). Toggle an engine ON to lock it — users see
            <em> "The model is currently locked. Please use other model."</em> and can't run it. Admins are never locked out.
          </p>
        </div>
      </div>

      <div className="card" data-testid="model-locks-card" style={{ padding: "0.5rem 1.2rem" }}>
        {MODEL_LOCK_DEFS.map((d) => renderRow({ ...d, lock: true }))}
      </div>
    </div>
  );
}
